import { startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import T from '@wojtekmaj/react-t';

import { getAccountName } from '@rewardopl/utils/account';
import { post } from '@rewardopl/utils/network';

import { Label, Wrapper } from './account_menu.styles';

import AccountAvatar from '../../common/account_avatar';
import MenuButton from '../../common/menu_button';

import useAccountPermission from '../../../hooks/useAccountPermission';

import { isRewardoAdmin } from '../../../helpers/authorization';

import {
  currentAccountState,
  integrationsState,
  maybeCurrentAccountRoleState,
  maybeCurrentAccountState,
  maybeCurrentBusinessState,
} from '../../../store';

type AccountMenuContentProps = {
  shouldDisplaySelectIntegration?: boolean;
};

function AccountMenuContent({ shouldDisplaySelectIntegration }: AccountMenuContentProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentAccount = useAtomValue(currentAccountState);
  const setMaybeCurrentAccount = useSetAtom(maybeCurrentAccountState);
  const setMaybeCurrentAccountRole = useSetAtom(maybeCurrentAccountRoleState);
  const setMaybeCurrentBusiness = useSetAtom(maybeCurrentBusinessState);

  const shouldAllowEdit = useAccountPermission('accounts_modify');

  async function logout() {
    await post('/api/accounts/logout');

    startTransition(() => {
      setMaybeCurrentAccount(null);
      setMaybeCurrentAccountRole(null);

      setTimeout(() => {
        navigate('/login');
        setTimeout(() => setMaybeCurrentBusiness(null), 100);
      }, 0);
    });
  }

  return (
    <MenuButton
      items={[
        shouldAllowEdit
          ? {
              label: 'Edit account',
              to: `/accounts/${currentAccount._id}/edit`,
            }
          : null,
        shouldDisplaySelectIntegration
          ? {
              label: 'Select integration',
              to: '#select-integration',
              state: location.state,
            }
          : null,
        {
          label: 'Settings',
          to: '/settings',
        },
        {
          label: 'Log out',
          onSelect: logout,
        },
      ].filter(Boolean)}
      label={
        <Wrapper>
          <AccountAvatar account={currentAccount} size={40} />
          <Label>
            <T>{getAccountName(currentAccount)}</T>
          </Label>
        </Wrapper>
      }
    />
  );
}

function AccountMenuContentWithIntegrations() {
  const currentAccount = useAtomValue(currentAccountState);
  const integrations = useAtomValue(integrationsState);
  const hasAtLeastOneIntegration =
    !isRewardoAdmin(currentAccount) && Boolean(integrations && integrations.length >= 1);

  const shouldDisplaySelectIntegration = hasAtLeastOneIntegration;

  return <AccountMenuContent shouldDisplaySelectIntegration={shouldDisplaySelectIntegration} />;
}

export default function AccountMenu() {
  const shouldDisplayIntegrations = useAccountPermission('integrations_view');

  return shouldDisplayIntegrations ? (
    <AccountMenuContentWithIntegrations />
  ) : (
    <AccountMenuContent />
  );
}
